import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaCopy, FaEye, FaCheck, FaTimes, FaSearch, FaLink } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  listTemplates,
  duplicateTemplate,
  toggleActive,
  Template,
  TemplateListParams,
} from '../services/TemplatesService';
import { TemplateEditorDrawer } from './TemplateEditorDrawer';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { WorkAreaAttachmentsModal } from './WorkAreaAttachmentsModal';

export function TemplatesAdminPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'global' | 'tenant'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [languageFilter, setLanguageFilter] = useState<'all' | 'de' | 'en'>('all');
  const [actionLoading, setActionLoading] = useState<Record<number, string>>({});

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] = useState<Template | null>(null);
  const [workAreaModalOpen, setWorkAreaModalOpen] = useState(false);
  const [workAreaTemplate, setWorkAreaTemplate] = useState<Template | null>(null);
  const [openActionMenuFor, setOpenActionMenuFor] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{top: number, left: number} | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // Nur nicht-"all" Filter senden (keine Null-Parameter)
      const params: any = {};

      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (scopeFilter !== 'all') params.scope = scopeFilter;
      if (activeFilter === 'active') params.active = true;
      if (activeFilter === 'inactive') params.active = false;
      if (languageFilter !== 'all') params.language = languageFilter;

      const response = await listTemplates(params);
      setTemplates(response.results || []);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(
        `Failed to load templates${traceId ? ` (Trace: ${traceId})` : ''}`
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setOpenActionMenuFor(null);
        setActionMenuPos(null);
      }
    };
    if (openActionMenuFor !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenuFor]);

  const handleSearch = () => {
    loadTemplates();
  };

  const handleCreate = () => {
    setEditingTemplateId(null);
    setEditorOpen(true);
  };

  const handleEdit = (template: Template) => {
    setEditingTemplateId(template.id);
    setEditorOpen(true);
  };

  const handlePreview = (template: Template) => {
    setPreviewingTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTemplateId(null);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewingTemplate(null);
  };

  const handleWorkAreaAttachments = (template: Template) => {
    setWorkAreaTemplate(template);
    setWorkAreaModalOpen(true);
  };

  const handleWorkAreaModalClose = () => {
    setWorkAreaModalOpen(false);
    setWorkAreaTemplate(null);
  };

  const handleDuplicate = async (template: Template) => {
    const confirmed = window.confirm(
      `Duplicate template "${template.name}"? This will create a tenant copy.`
    );
    if (!confirmed) return;

    setActionLoading((prev) => ({ ...prev, [template.id]: 'duplicating' }));
    try {
      await duplicateTemplate(template.id);
      toast.success('Template duplicated successfully');
      loadTemplates();
    } catch (err: any) {
      console.error('Failed to duplicate template:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(
        `Failed to duplicate template${traceId ? ` (Trace: ${traceId})` : ''}`
      );
    } finally {
      setActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[template.id];
        return newState;
      });
    }
  };

  const handleToggleActive = async (template: Template) => {
    const action = template.active ? 'deactivate' : 'activate';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} "${template.name}"?`
    );
    if (!confirmed) return;

    setActionLoading((prev) => ({ ...prev, [template.id]: action }));
    try {
      await toggleActive(template.id, !template.active);
      toast.success(`Template ${action}d successfully`);
      loadTemplates();
    } catch (err: any) {
      console.error(`Failed to ${action} template:`, err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(
        `Failed to ${action} template${traceId ? ` (Trace: ${traceId})` : ''}`
      );
    } finally {
      setActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[template.id];
        return newState;
      });
    }
  };

  type EmployeesTabKey = 'employees' | 'payrolls' | 'documents' | 'recruiting';
  const TABS: { key: EmployeesTabKey; label: string; to: string }[] = [
    { key: 'employees', label: 'Employees', to: '/employees' },
    { key: 'payrolls', label: 'Payrolls', to: '/employees/payrolls' },
    { key: 'documents', label: 'Documents', to: '/employees/documents' },
    { key: 'recruiting', label: 'Recruiting', to: '/employees/recruiting' },
  ];
  const activeTab: EmployeesTabKey = 'documents';

  return (
    <div style={{ padding: '16px', position: 'relative', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Glass Header */}
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100 }}
      >
        {/* Row 1: Tabs (left) + Add Button (right) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.to)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? '#5271A5' : '#64748b',
                    borderRadius: '9999px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = '#5271A5';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#64748b';
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Create Template Button */}
          <button
            onClick={handleCreate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              background: 'linear-gradient(135deg, #5271A5 0%, #3b5998 50%, #2c4a80 100%)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              fontWeight: 600,
              fontSize: '11px',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(82, 113, 165, 0.25)',
              transition: 'all 0.2s ease',
            }}
          >
            <FaPlus style={{ fontSize: '9px' }} /> Template
          </button>
        </div>

        {/* Row 2: Filter Bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: '42px' }}>
          {/* Search Input */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search..."
            style={{
              padding: '6px 12px',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              fontSize: '11px',
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#334155',
              width: '160px',
              outline: 'none',
            }}
          />

          {/* Scope Filter */}
          <select
            value={scopeFilter}
            onChange={(e) => { setScopeFilter(e.target.value as any); handleSearch(); }}
            style={{
              padding: '6px 12px',
              borderRadius: '9999px',
              border: scopeFilter !== 'all' ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid #e5e7eb',
              fontSize: '11px',
              fontWeight: 600,
              background: scopeFilter !== 'all' ? 'rgba(82, 113, 165, 0.12)' : 'rgba(255, 255, 255, 0.9)',
              color: scopeFilter !== 'all' ? '#5271A5' : '#334155',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Scopes</option>
            <option value="global">Global</option>
            <option value="tenant">Tenant</option>
          </select>

          {/* Status Filter */}
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value as any); handleSearch(); }}
            style={{
              padding: '6px 12px',
              borderRadius: '9999px',
              border: activeFilter !== 'all' ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid #e5e7eb',
              fontSize: '11px',
              fontWeight: 600,
              background: activeFilter !== 'all' ? 'rgba(82, 113, 165, 0.12)' : 'rgba(255, 255, 255, 0.9)',
              color: activeFilter !== 'all' ? '#5271A5' : '#334155',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Language Filter */}
          <select
            value={languageFilter}
            onChange={(e) => { setLanguageFilter(e.target.value as any); handleSearch(); }}
            style={{
              padding: '6px 12px',
              borderRadius: '9999px',
              border: languageFilter !== 'all' ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid #e5e7eb',
              fontSize: '11px',
              fontWeight: 600,
              background: languageFilter !== 'all' ? 'rgba(82, 113, 165, 0.12)' : 'rgba(255, 255, 255, 0.9)',
              color: languageFilter !== 'all' ? '#5271A5' : '#334155',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Languages</option>
            <option value="de">German</option>
            <option value="en">English</option>
          </select>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              borderRadius: '9999px',
              border: 'none',
              fontSize: '11px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #5271A5 0%, #3b5998 50%, #2c4a80 100%)',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 6px rgba(82, 113, 165, 0.25)',
              transition: 'all 0.15s',
            }}
          >
            <FaSearch size={10} />
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* Table Container */}
        <div
          className="glass-container"
          style={{ overflow: 'hidden', borderRadius: 16 }}
        >
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 11 }}>
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
              No templates found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: 'none' }}>
                    {['Name', 'Key', 'Lang', 'Scope', 'Status', '', 'Updated', 'Actions'].map((header, i) => (
                      <th
                        key={header || i}
                        style={{
                          padding: '8px 6px',
                          textAlign: i === 7 ? 'right' : (i >= 2 && i <= 5) ? 'center' : 'left',
                          position: 'sticky',
                          top: 0,
                          background: '#f9fafb',
                          zIndex: 2,
                          fontWeight: 600,
                          fontSize: 10,
                          color: '#64748b',
                          letterSpacing: '0.2px',
                          border: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr
                      key={template.id}
                      style={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                        transition: 'background 0.15s',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '6px', fontWeight: 500, color: '#334155' }}>
                        {template.name}
                      </td>
                      <td style={{ padding: '6px', color: '#64748b', fontFamily: 'monospace', fontSize: 10 }}>
                        {template.template_key}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: template.language === 'de' ? 'rgba(82, 113, 165, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                            color: template.language === 'de' ? '#5271A5' : '#7c3aed',
                          }}
                        >
                          {template.language.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: template.scope === 'global' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: template.scope === 'global' ? '#d97706' : '#059669',
                          }}
                        >
                          {template.scope}
                        </span>
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: template.active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: template.active ? '#059669' : '#dc2626',
                          }}
                        >
                          {template.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        {template.contains_compensation && (
                          <span style={{ fontSize: 12 }}>🔒</span>
                        )}
                      </td>
                      <td style={{ padding: '6px', color: '#64748b', fontSize: 10 }}>
                        {new Date(template.updated_at).toLocaleDateString('de-DE')}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openActionMenuFor === template.id) {
                              setOpenActionMenuFor(null);
                              setActionMenuPos(null);
                            } else {
                              setOpenActionMenuFor(template.id);
                              const rect = e.currentTarget.getBoundingClientRect();
                              setActionMenuPos({ top: rect.bottom + 4, left: rect.right });
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 16,
                            color: '#64748b',
                            padding: '4px 8px',
                            borderRadius: 6,
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(82, 113, 165, 0.1)';
                            e.currentTarget.style.color = '#5271A5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#64748b';
                          }}
                          title="Actions"
                        >
                          ⋮
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <TemplateEditorDrawer
        isOpen={editorOpen}
        onClose={handleEditorClose}
        templateId={editingTemplateId}
        onSaved={loadTemplates}
      />

      <TemplatePreviewModal
        isOpen={previewModalOpen}
        onClose={handlePreviewClose}
        template={previewingTemplate}
      />

      <WorkAreaAttachmentsModal
        isOpen={workAreaModalOpen}
        onClose={handleWorkAreaModalClose}
        template={workAreaTemplate}
      />

      {/* Action Menu (portaled to body to escape overflow clipping) */}
      {openActionMenuFor !== null && actionMenuPos && createPortal(
        (() => {
          const template = templates.find(t => t.id === openActionMenuFor);
          if (!template) return null;
          const closeMenu = () => { setOpenActionMenuFor(null); setActionMenuPos(null); };
          const menuBtnStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            background: 'transparent',
            color: '#334155',
            fontSize: 11,
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: 10,
            transition: 'all 0.15s',
          };
          const hoverIn = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'rgba(82, 113, 165, 0.1)'; e.currentTarget.style.color = '#5271A5'; };
          const hoverOut = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; };
          return (
            <div
              ref={actionMenuRef}
              style={{
                position: 'fixed',
                top: actionMenuPos.top,
                right: window.innerWidth - actionMenuPos.left,
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: 8,
                minWidth: 140,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                zIndex: 10000,
              }}
            >
              <button onClick={() => { handleEdit(template); closeMenu(); }} style={menuBtnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                <FaEdit size={10} /> Edit
              </button>
              <button onClick={() => { handlePreview(template); closeMenu(); }} style={menuBtnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                <FaEye size={10} /> Preview
              </button>
              <button
                onClick={() => { handleDuplicate(template); closeMenu(); }}
                disabled={!!actionLoading[template.id]}
                style={{ ...menuBtnStyle, cursor: actionLoading[template.id] ? 'not-allowed' : 'pointer', opacity: actionLoading[template.id] ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (!actionLoading[template.id]) hoverIn(e); }}
                onMouseLeave={hoverOut}
              >
                <FaCopy size={10} /> Duplicate
              </button>
              {(template.category === 'ANNEX' || template.template_key?.startsWith('ANNEX_')) && (
                <button onClick={() => { handleWorkAreaAttachments(template); closeMenu(); }} style={menuBtnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                  <FaLink size={10} /> Work Areas
                </button>
              )}
              <div style={{ height: 1, background: 'rgba(0, 0, 0, 0.06)', margin: '4px 0' }} />
              <button
                onClick={() => { handleToggleActive(template); closeMenu(); }}
                disabled={!!actionLoading[template.id]}
                style={{
                  ...menuBtnStyle,
                  color: template.active ? '#dc2626' : '#059669',
                  cursor: actionLoading[template.id] ? 'not-allowed' : 'pointer',
                  opacity: actionLoading[template.id] ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (!actionLoading[template.id]) { e.currentTarget.style.background = template.active ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {template.active ? <FaTimes size={10} /> : <FaCheck size={10} />}
                {template.active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
}
